/** 策略与参数（只读，屏规格 `docs/design/screens/strategy.md`，#4598）。
 *
 * 策略清单 `GET /strategies`（来源线、起始资金、来源节奏 1 天 / 3 天、是否超出节奏）+ `GET /paper/state`；
 * 撮合口径 `GET /config/simulation`；三处同一配置 `GET /runs`；信号来源 `GET /signals/inbox`。
 * 参数集注册表是候选能力（主人 2026-10-08 裁决），本屏不画参数集列。
 */

import { NavLink } from "react-router";

import { useRuns, useStatus } from "../../api/queries";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { money } from "../../lib/format";
import { digestGroups, latestInboxByStrategy } from "../../lib/g1";
import { useInboxIndex, usePaperState, useSimulationConfig, useStrategies } from "../../lib/g1Queries";
import { Load } from "../overview/parts";

export function StrategyScreen(): React.ReactNode {
  const strategies = useStrategies();
  const paper = usePaperState();
  const sim = useSimulationConfig();
  const status = useStatus();
  const runs = useRuns();
  const inbox = useInboxIndex();
  const current = status.data?.config_digest ?? null;
  return (
    <>
      <PageHead title="策略与参数" sub="GET /strategies · 只读" />
      <Lede>在跑的是哪几条策略、各自的信号从哪条线来、按什么撮合口径成交、三处运行是不是同一份配置。改参数与出信号归投研侧，本屏没有入口。</Lede>
      <Card title="策略清单" sub="每条信号线一本账 · 来源节奏" nopad>
        <Load q={strategies} what="策略清单">
          {(rows) => (
            <Table
              cols={[
                { label: "策略" },
                { label: "来源线" },
                { label: "起始资金", num: true },
                { label: "起始日" },
                { label: "模式" },
                { label: "节奏" },
                { label: "最近信号日" },
                { label: "节奏判定" },
                { label: "停机" },
                { label: "最后交易日" },
              ]}
              rows={rows.map((row) => {
                const ps = paper.data?.strategies[row.strategy_id];
                return {
                  key: row.strategy_id,
                  cls: ps?.halted ? "bad" : row.stale ? "warn" : "",
                  cells: [
                    row.strategy_id,
                    row.source_line,
                    money(row.starting_balance),
                    row.inception_date ?? "—",
                    row.modes.join(" / "),
                    row.cadence_days === null ? "—" : `${row.cadence_days} 个交易日`,
                    row.last_signal_date ?? "—（未见信号）",
                    row.stale === null ? "—" : row.stale ? <Pill tone="warn">超出节奏（上游疑似停产）</Pill> : <Pill tone="ok">在节奏内</Pill>,
                    paper.isError ? "未取到" : ps ? (ps.halted ? <span className="neg">停机：{ps.halt_reason ?? "—"} <NavLink to="/halt">去停机屏</NavLink></span> : "否") : "—",
                    ps?.last_trade_date ?? "—",
                  ],
                };
              })}
              empty="config/paper.json 里没有策略"
            />
          )}
        </Load>
        <Note>节奏判定按截至 {strategies.data?.[0]?.stale_reference_date ?? "—"} 的交易日数计算（someopark 每 1 天、someopark-test 每 3 天，主人 2026-10-08 裁决）；本屏只显示，不另算处置。参数集注册表是候选能力，不在本屏。</Note>
      </Card>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="撮合口径" sub="GET /config/simulation">
          <Load q={sim} what="撮合假设">
            {(data) => {
              const fee = (data.body["fee"] ?? {}) as Record<string, unknown>;
              const slip = (data.body["slippage"] ?? {}) as Record<string, unknown>;
              return (
                <dl className="kv">
                  <dt>口径指纹</dt>
                  <dd>
                    {data.digest} {current && current !== data.digest ? <span className="neg">（与 /status 的 {current} 不一致）</span> : null}
                  </dd>
                  <dt>手续费</dt>
                  <dd>
                    每股 {String(fee["per_share"] ?? "—")}，最低 {String(fee["min_per_order"] ?? "—")}，上限成交额 {String(fee["max_pct_of_value"] ?? "—")}
                  </dd>
                  <dt>滑点</dt>
                  <dd>{String(slip["bps"] ?? "—")} bps</dd>
                  <dt>执行时点</dt>
                  <dd>
                    {data.execution.signal_at} → {data.execution.fill_at}；当天不成交
                  </dd>
                  <dt>完整文件</dt>
                  <dd>
                    <NavLink to="/settings">去设置屏</NavLink>
                  </dd>
                </dl>
              );
            }}
          </Load>
        </Card>
        <Card title="三处同一配置" sub="GET /runs · 按模式分组的 config_digest_canonical" nopad>
          <Load q={runs} what="运行索引">
            {(list) => {
              const rows = digestGroups(list, current).map((g) => ({
                key: `${g.mode}-${g.digest}`,
                cls: g.consistent === false ? "warn" : "",
                cells: [g.mode, g.digest, g.count, g.consistent ? <Pill tone="ok">与当前一致</Pill> : <Pill tone="warn">不同</Pill>],
              }));
              return <Table cols={[{ label: "模式" }, { label: "指纹" }, { label: "运行数", num: true }, { label: "与当前" }]} rows={rows} empty="还没有运行记录" />;
            }}
          </Load>
        </Card>
      </div>
      <Card title="信号来源（最近一次桥接）" sub="GET /signals/inbox" nopad>
        <Load q={inbox} what="收件箱索引">
          {(rows) => {
            const latest = latestInboxByStrategy(rows);
            return (
              <Table
                cols={[{ label: "策略" }, { label: "日期" }, { label: "受理", num: true }, { label: "拒收", num: true }, { label: "不下单", num: true }, { label: "说明" }]}
                rows={[...latest.values()].map((row) => ({
                  key: row.strategy_id,
                  cells: [row.strategy_id, row.date, row.accepted, row.rejected, row.no_order, row.accepted === 0 && row.no_order > 0 ? "上游当天无可下单信号" : row.accepted === 0 ? "当天无信号" : "—"],
                }))}
                empty="还没有桥接产物"
              />
            );
          }}
        </Load>
        <Note>翻译留痕明细（输入指纹、version_note、拒收原因）在「信号收件箱」屏；翻译规则版本 rules_version：缺（未提供）。</Note>
      </Card>
    </>
  );
}
